/**
 * PropertiesPlugin.ts
 * @copyright Microsoft 2018
 */
import { CoreUtils } from '@microsoft/applicationinsights-core-js';
import { ContextTagKeys, PageView, ConfigurationManager, partAExtensions } from '@microsoft/applicationinsights-common';
import { Session, _SessionManager } from './Context/Session';
import { Application } from './Context/Application';
import { Device } from './Context/Device';
import { Internal } from './Context/Internal';
import { Location } from './Context/Location';
import { Operation } from './Context/Operation';
import { User } from './Context/User';
import { Sample } from './Context/Sample';
var PropertiesPlugin = /** @class */ (function () {
    function PropertiesPlugin() {
        this.priority = 170;
        this.identifier = "AppInsightsPropertiesPlugin";
    }
    PropertiesPlugin.getDefaultConfig = function () {
        var defaultConfig = {
            instrumentationKey: function () { return undefined; },
            accountId: function () { return null; },
            sessionRenewalMs: function () { return 30 * 60 * 1000; },
            samplingPercentage: function () { return 100; },
            sessionExpirationMs: function () { return 24 * 60 * 60 * 1000; },
            cookieDomain: function () { return null; },
            sdkExtension: function () { return null; },
            isBrowserLinkTrackingEnabled: function () { return false; },
            appId: function () { return null; },
            enableOldTags: function () { return false; }
        };
        return defaultConfig;
    };
    PropertiesPlugin.prototype.initialize = function (config, core, extensions) {
        var _this = this;
        var defaultConfig = PropertiesPlugin.getDefaultConfig();
        this._extensionConfig = this._extensionConfig || PropertiesPlugin.getDefaultConfig();
        var _loop_1 = function (field) {
            this_1._extensionConfig[field] = function () { return ConfigurationManager.getConfig(config, field, _this.identifier, defaultConfig[field]()); };
        };
        var this_1 = this;
        for (var field in defaultConfig) {
            _loop_1(field);
        }
        if (typeof window !== 'undefined') {
            this._sessionManager = new _SessionManager(this._extensionConfig, core.logger);
            this.application = new Application();
            this.device = new Device();
            this.internal = new Internal(this._extensionConfig);
            this.location = new Location();
            this.user = new User(this._extensionConfig, core.logger);
            this.operation = new Operation();
            this.session = new Session();
            this.sample = new Sample(this._extensionConfig.samplingPercentage(), core.logger);
        }
    };
    /**
     * Add Part A fields to the event
     * @param event The event that needs to be processed
     */
    PropertiesPlugin.prototype.processTelemetry = function (event) {
        if (CoreUtils.isNullOrUndefined(event)) {
            // TODO(barustum): throw an internal event once we have support for internal logging
        }
        else {
            // if the event is not sampled in, do not bother going through the pipeline
            if (this.sample.isSampledIn(event)) {
                // If the envelope is PageView, reset the internal message count so that we can send internal telemetry for the new page.
                if (event.name === PageView.envelopeType) {
                    // TODO(barustum): resetInternalMessageCount once we have support for internal logging
                    //_InternalLogging.resetInternalMessageCount();
                }
                if (this.session) {
                    // If customer did not provide custom session id update the session manager
                    if (typeof this.session.id !== "string") {
                        this._sessionManager.update();
                    }
                }
                this._processTelemetryInternal(event);
            }
            if (!CoreUtils.isNullOrUndefined(this._nextPlugin)) {
                this._nextPlugin.processTelemetry(event);
            }
        }
    };
    /**
     * Sets the next plugin that comes after this plugin
     * @param nextPlugin The next plugin
     */
    PropertiesPlugin.prototype.setNextPlugin = function (nextPlugin) {
        this._nextPlugin = nextPlugin;
    };
    PropertiesPlugin.prototype._processTelemetryInternal = function (event) {
        var tagsItem = {};
        if (this.session) {
            // If customer set id, apply his context; otherwise apply context generated from cookies 
            if (typeof this.session.id === "string") {
                PropertiesPlugin._applySessionContext(tagsItem, this.session);
            }
            else {
                PropertiesPlugin._applySessionContext(tagsItem, this._sessionManager.automaticSession);
            }
        }
        if (this._extensionConfig.enableOldTags()) {
            // set part A  fields
            PropertiesPlugin._applyApplicationContext(tagsItem, this.application);
            PropertiesPlugin._applyDeviceContext(tagsItem, this.device);
            PropertiesPlugin._applyInternalContext(tagsItem, this.internal);
            PropertiesPlugin._applyLocationContext(tagsItem, this.location);
            PropertiesPlugin._applySampleContext(tagsItem, this.sample);
            PropertiesPlugin._applyOperationContext(tagsItem, this.operation);
        }
        this._applyUserContext(event, this.user);
        var tgs = [];
        Object.keys(tagsItem).forEach(function (item) {
            var p = {};
            p[item] = tagsItem[item];
            tgs.push(p);
        });
        event.tags.forEach(function (item) {
            tgs.push(item);
        });
        event.tags = tgs;
    };
    PropertiesPlugin._applySessionContext = function (tags, sessionContext) {
        if (sessionContext) {
            var tagKeys = new ContextTagKeys();
            if (typeof sessionContext.id === "string") {
                tags[tagKeys.sessionId] = sessionContext.id;
            }
            if (typeof sessionContext.isFirst !== "undefined") {
                tags[tagKeys.sessionIsFirst] = sessionContext.isFirst;
            }
        }
    };
    PropertiesPlugin._applyApplicationContext = function (tagsItem, appContext) {
        if (appContext) {
            var tagKeys = new ContextTagKeys();
            if (typeof appContext.ver === "string") {
                tagsItem[tagKeys.applicationVersion] = appContext.ver;
            }
            if (typeof appContext.build === "string") {
                tagsItem[tagKeys.applicationBuild] = appContext.build;
            }
        }
    };
    PropertiesPlugin._applyDeviceContext = function (tagsItem, deviceContext) {
        var tagKeys = new ContextTagKeys();
        if (deviceContext) {
            if (typeof deviceContext.id === "string") {
                tagsItem[tagKeys.deviceId] = deviceContext.id;
            }
            if (typeof deviceContext.ip === "string") {
                tagsItem[tagKeys.deviceIp] = deviceContext.ip;
            }
            if (typeof deviceContext.language === "string") {
                tagsItem[tagKeys.deviceLanguage] = deviceContext.language;
            }
            if (typeof deviceContext.locale === "string") {
                tagsItem[tagKeys.deviceLocale] = deviceContext.locale;
            }
            if (typeof deviceContext.model === "string") {
                tagsItem[tagKeys.deviceModel] = deviceContext.model;
            }
            if (typeof deviceContext.network !== "undefined") {
                tagsItem[tagKeys.deviceNetwork] = deviceContext.network;
            }
            if (typeof deviceContext.oemName === "string") {
                tagsItem[tagKeys.deviceOEMName] = deviceContext.oemName;
            }
            if (typeof deviceContext.os === "string") {
                tagsItem[tagKeys.deviceOS] = deviceContext.os;
            }
            if (typeof deviceContext.osversion === "string") {
                tagsItem[tagKeys.deviceOSVersion] = deviceContext.osversion;
            }
            if (typeof deviceContext.resolution === "string") {
                tagsItem[tagKeys.deviceScreenResolution] = deviceContext.resolution;
            }
            if (typeof deviceContext.type === "string") {
                tagsItem[tagKeys.deviceType] = deviceContext.type;
            }
        }
    };
    PropertiesPlugin._applyInternalContext = function (tagsItem, internalContext) {
        if (internalContext) {
            var tagKeys = new ContextTagKeys();
            if (typeof internalContext.agentVersion === "string") {
                tagsItem[tagKeys.internalAgentVersion] = internalContext.agentVersion;
            }
            if (typeof internalContext.sdkVersion === "string") {
                tagsItem[tagKeys.internalSdkVersion] = internalContext.sdkVersion;
            }
        }
    };
    PropertiesPlugin._applyLocationContext = function (tagsItem, locationContext) {
        if (locationContext) {
            var tagKeys = new ContextTagKeys();
            if (typeof locationContext.ip === "string") {
                tagsItem[tagKeys.locationIp] = locationContext.ip;
            }
        }
    };
    PropertiesPlugin._applySampleContext = function (tagsItem, sampleContext) {
        if (sampleContext) {
            tagsItem.sampleRate = sampleContext.sampleRate;
        }
    };
    PropertiesPlugin._applyOperationContext = function (tagsItem, operationContext) {
        if (operationContext) {
            var tagKeys = new ContextTagKeys();
            if (typeof operationContext.id === "string") {
                tagsItem[tagKeys.operationId] = operationContext.id;
            }
            if (typeof operationContext.name === "string") {
                tagsItem[tagKeys.operationName] = operationContext.name;
            }
            if (typeof operationContext.parentId === "string") {
                tagsItem[tagKeys.operationParentId] = operationContext.parentId;
            }
            if (typeof operationContext.rootId === "string") {
                tagsItem[tagKeys.operationRootId] = operationContext.rootId;
            }
            if (typeof operationContext.syntheticSource === "string") {
                tagsItem[tagKeys.operationSyntheticSource] = operationContext.syntheticSource;
            }
        }
    };
    PropertiesPlugin.prototype._applyUserContext = function (event, userContext) {
        if (userContext) {
            if (!event.tags) {
                event.tags = [];
            }
            var tagKeys = new ContextTagKeys();
            if (this._extensionConfig.enableOldTags()) {
                if (typeof userContext.agent === "string") {
                    var val = userContext.agent;
                    var ky = tagKeys.userAgent;
                    event.tags.push({ ky: val });
                }
                if (typeof userContext.storeRegion === "string") {
                    var ky = tagKeys.userStoreRegion;
                    var val = userContext.storeRegion;
                    event.tags.push({ ky: val });
                }
            }
            // stays in tags under User extension
            if (typeof userContext.accountId === "string") {
                var item = {};
                item[partAExtensions.accountIdTag] = userContext.accountId;
                event.tags.push(item);
            }
            var ctxExt = {};
            // CS 4.0            
            if (typeof userContext.id === "string") {
                ctxExt.localId = userContext.id;
            }
            if (typeof userContext.authenticatedId === "string") {
                ctxExt.authId = userContext.authenticatedId;
            }
            event.ctx[partAExtensions.UserExtensionName] = ctxExt; // part A extension
            // CS 4.0
        }
    };
    return PropertiesPlugin;
}());
export default PropertiesPlugin;
//# sourceMappingURL=PropertiesPlugin.js.map